import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Home.css";

const Home = () => {
  const navigate = useNavigate();
  const username = localStorage.getItem("username") || "Administrator";

  const [stats, setStats] = useState({
    totalClients: 0,
    active: 0,
    expired: 0,
    trial: 0,
    online: 0,
    offline: 0,
  });
  const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("username");
    navigate("/login");
  };

  const fetchStats = async () => {
    try {
      setLoading(true);

      const licRes = await axios.get("/api/licenses/stats");
      const licData = licRes.data?.data || licRes.data || {};

      const srvRes = await axios.get("/api/servers/list");
      const servers = srvRes.data?.data || srvRes.data || [];

      const online = servers.filter((s) => s.status === "active").length;
      const offline = servers.filter((s) => s.status !== "active").length;

      const cliRes = await axios.get("/api/clients/list");
      const clientsData = cliRes.data?.data || cliRes.data || [];

      setStats({
        totalClients: clientsData.length || 0,
        active: licData.active ?? 0,
        expired: licData.expired ?? 0,
        trial: licData.trial ?? 0,
        online,
        offline,
      });
    } catch (err) {
      console.error("Dashboard core metrics fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="key-manage-dashboard">

      {/* HEADER */}
      <div className="key-manage-header">
        <div>
          <h1>LIC ADMIN UI</h1>
          <p className="dashboard-user">
            Welcome back, <strong>{username}</strong>
          </p>
        </div>
        <button className="logout-btn" onClick={handleLogout}>
          Logout Panel
        </button>
      </div>

      {/* STATS CARDS */}
      <div className="key-manage-grid">
        <div className="key-manage-card key-manage-blue" onClick={() => navigate("/manage-clients")}>
          <h2>{loading ? "..." : stats.totalClients}</h2>
          <p>Total Clients</p>
        </div>

        <div className="key-manage-card key-manage-green" onClick={() => navigate("/manage-license")}>
          <h2>{loading ? "..." : stats.active}</h2>
          <p>Active Licenses</p>
        </div>

        <div className="key-manage-card key-manage-red" onClick={() => navigate("/manage-license")}>
          <h2>{loading ? "..." : stats.expired}</h2>
          <p>Expired Licenses</p>
        </div>

        <div className="key-manage-card key-manage-orange" onClick={() => navigate("/manage-license")}>
          <h2>{loading ? "..." : stats.trial}</h2>
          <p>Trial Licenses</p>
        </div>

        <div className="key-manage-card key-manage-cyan" onClick={() => navigate("/manage-server")}>
          <h2>{loading ? "..." : stats.online}</h2>
          <p>Online Servers</p>
        </div>

        <div className="key-manage-card key-manage-gray" onClick={() => navigate("/manage-server")}>
          <h2>{loading ? "..." : stats.offline}</h2>
          <p>Offline Servers</p>
        </div>
      </div>

      {/* QUICK ACTIONS */}
      <div className="key-manage-section-title">
        <h3>Quick Operations</h3>
        <p>Direct entry points for client & license management</p>
      </div>

      <div className="key-manage-actions-grid">
        {/* Manage Clients */}
        <div className="key-manage-action-card client-action" onClick={() => navigate("/manage-clients")}>
          <div className="key-manage-action-icon-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <div className="key-manage-action-info">
            <h4>Manage Clients</h4>
            <p>View customer accounts, register new clients and generate enrollment tokens.</p>
          </div>
          <span className="key-manage-action-arrow">→</span>
        </div>

        {/* Manage Licenses */}
        <div className="key-manage-action-card license-action" onClick={() => navigate("/manage-license")}>
          <div className="key-manage-action-icon-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <div className="key-manage-action-info">
            <h4>Manage Licenses</h4>
            <p>Renew or extend contracts, adjust channel limits and freeze or activate licenses.</p>
          </div>
          <span className="key-manage-action-arrow">→</span>
        </div>

        {/* Manage Servers */}
        <div className="key-manage-action-card server-action" onClick={() => navigate("/manage-server")}>
          <div className="key-manage-action-icon-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
          </div>
          <div className="key-manage-action-info">
            <h4>Manage Servers</h4>
            <p>Monitor enrolled client nodes with live CPU, RAM, disk and heartbeat status.</p>
          </div>
          <span className="key-manage-action-arrow">→</span>
        </div>
      </div>

    </div>
  );
};

export default Home;